import React, { useEffect, useMemo, useState } from 'react';
import { Tour, Modal, Row, Col, Card, Typography, Spin, Button, ConfigProvider } from 'antd';
import { TagOutlined, PictureOutlined, ThunderboltOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import zhCN from 'antd/locale/zh_CN';
import { ROUTER_INDEX_PAGE, ROUTER_TEMPLATE_LIST } from '@/entry/route';
import { TemplateType } from '@shared/constants/template';
import './styles.less';
import LocalStorage from '@shared/utils/localStorage';

const { Title, Paragraph, Text } = Typography;

interface GuideModalProps {
  open: boolean;
  onClose: () => void;
  onStart?: () => void;
  product: string;
}

const prefixCls = 'guide-modal';

const GuideModal: React.FC<GuideModalProps> = ({ open, onClose, onStart, product }) => {
  const [current, setCurrent] = useState(0);
  const [tourOpen, setTourOpen] = useState(false);
  const [choice, setChoice] = useState<'label' | 'ecommerce' | 'ai' | null>(null);
  const [loading, setLoading] = useState<'label' | 'ecommerce' | 'ai' | null>(null);
  const [guideModalOpen, setGuideModalOpen] = useState(open);
  const navigate = useNavigate();

  // 增强版 waitForElement：检查元素存在且可见
  const waitForElement = (id: string, tries = 20, interval = 200) => new Promise<void>((resolve) => {
    let count = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      if (el && el.offsetParent !== null) { // offsetParent !== null 意味着元素可见
        clearInterval(timer);
        resolve();
      } else if (++count >= tries) {
        clearInterval(timer);
        // 即使超时也 resolve，让 Tour 尝试运行（Tour 自身有容错）
        resolve();
      }
    }, interval);
  });

  const steps = useMemo(() => {
    const labelTarget = () => document.getElementById('tour-label-design');
    const ecommerceTarget = () => document.getElementById('tour-ecommerce-design');
    const aiTarget = () => document.getElementById('tour-ai-tools');
    
    if (choice === 'label') {
      return [{ 
        title: '跨境标签设计', 
        description: 'TEMU / Shein / TikTok 等多平台标准模板，一键套用。点击任意模板即可开始编辑。', 
        target: labelTarget,
        cover: <div className="tour-cover-icon"><TagOutlined /></div>
      }];
    }
    if (choice === 'ecommerce') {
      return [{ 
        title: '电商图片设计', 
        description: '选择主图规格（1:1 / 3:4），从丰富模板快速生成吸引人的商品图。', 
        target: ecommerceTarget,
        cover: <div className="tour-cover-icon"><PictureOutlined /></div>
      }];
    }
    if (choice === 'ai') {
      return [{ 
        title: 'AI工具箱，选择一个功能开始吧', 
        description: '集合AI商品图、智能抠图、图片翻译、AI扩图等黑科技，大幅提升设计效率。', 
        target: aiTarget,
        // cover: <div className="tour-cover-icon"><ThunderboltOutlined /></div>
      }];
    }
    return [];
  }, [choice]);

  useEffect(() => {
    setGuideModalOpen(open);
  }, [open]);

  useEffect(() => {
    LocalStorage.onUserGuideVisitedChange((isVisited) => {
      if (isVisited) {
        setTourOpen(false);
      }
    });
  }, []);

  const handleCardClick = async (key: 'label' | 'ecommerce' | 'ai', targetId: string) => {
    if (loading) return;
    setGuideModalOpen(false);
    
    if (key === 'label') {
      onClose();
      navigate(`${ROUTER_TEMPLATE_LIST}?templateType=${TemplateType.CrossBorderLabel}&from=guide`);
      return;
    }

    if (key === 'ecommerce') {
      onClose();
      navigate(`${ROUTER_TEMPLATE_LIST}?templateType=${TemplateType.ProductMainImage}&from=guide`);
      return;
    }

    setLoading(key);
    setChoice(key);
    
    // 导航到首页
    navigate(ROUTER_INDEX_PAGE);
    
    // 等待目标元素出现
    await waitForElement(targetId);
    
    setLoading(null);
    setTourOpen(true);
  };

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: { colorPrimary: '#f97316' },
        components: {
          Modal: {
            paddingContentHorizontalLG: 40,
            paddingMD: 40,
            borderRadiusLG: 16,
          }
        }
      }}
    >
      {/* 功能选择 Modal */}
      <Modal 
        open={guideModalOpen} 
        onCancel={onClose} 
        footer={null} 
        centered 
        destroyOnClose 
        width={900}
        className={prefixCls}
        maskClosable={false}
        closeIcon={<div className={`${prefixCls}-close-icon`}><CloseOutlined /></div>}
      >
        <div className={`${prefixCls}-header`}>
          <Title level={2} style={{ color: '#fff', marginBottom: 8 }}>欢迎使用 {product}</Title>
          <Paragraph style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16 }}>
            请选择您最感兴趣的功能，我们将带您快速上手
          </Paragraph>
        </div>
        
        <div className={`${prefixCls}-content`}>
          <Row gutter={[24, 24]} justify="center">
            <Col xs={24} md={8}>
              <Card 
                hoverable 
                className={`${prefixCls}-card ${prefixCls}-card-label ${loading === 'label' ? 'loading' : ''}`}
                onClick={() => handleCardClick('label', 'tour-label-design')}
              >
                <div className="card-icon-wrapper label-icon">
                  {loading === 'label' ? <Spin /> : <TagOutlined />}
                </div>
                <Title level={4} className="card-title">跨境标签设计</Title>
                <Paragraph className="card-desc" type="secondary">
                  TEMU / Shein / TikTok 等多平台标准模板，一键套用
                </Paragraph>
                <div className="card-footer">
                  <Text>去体验 <RightOutlined /></Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card 
                hoverable 
                className={`${prefixCls}-card ${prefixCls}-card-ecommerce ${loading === 'ecommerce' ? 'loading' : ''}`}
                onClick={() => handleCardClick('ecommerce', 'tour-ecommerce-design')}
              >
                <div className="card-icon-wrapper ecommerce-icon">
                  {loading === 'ecommerce' ? <Spin /> : <PictureOutlined />}
                </div>
                <Title level={4} className="card-title">电商图片设计</Title>
                <Paragraph className="card-desc" type="secondary">
                  主图规格（1:1 / 3:4）与海量营销模板集合
                </Paragraph>
                <div className="card-footer">
                  <Text>去体验 <RightOutlined /></Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card 
                hoverable 
                className={`${prefixCls}-card ${prefixCls}-card-ai ${loading === 'ai' ? 'loading' : ''}`}
                onClick={() => handleCardClick('ai', 'tour-ai-tools')}
              >
                <div className="card-icon-wrapper ai-icon">
                  {loading === 'ai' ? <Spin /> : <ThunderboltOutlined />}
                </div>
                <Title level={4} className="card-title">AI工具</Title>
                <Paragraph className="card-desc" type="secondary">
                  AI商品图 / 智能抠图 / 翻译 / 扩图
                </Paragraph>
                <div className="card-footer">
                  <Text>去体验 <RightOutlined /></Text>
                </div>
              </Card>
            </Col>
          </Row>
        </div>

        <div className={`${prefixCls}-footer`}>
          <Button type="link" onClick={onClose} style={{ color: '#999' }}>
            跳过引导，直接开始使用
          </Button>
        </div>
      </Modal>

      {/* 引导 Tour */}
      <Tour
        open={tourOpen}
        current={current}
        onChange={setCurrent}
        onClose={() => {
          setTourOpen(false);
          setChoice(null);
          if (onStart) onStart();
          onClose(); // 完全关闭
        }}
        steps={steps as any}
        mask={{
          style: { boxShadow: 'inset 0 0 15px #333' },
          color: 'rgba(0, 0, 0, .5)',
        }}
        type="primary"
      />
    </ConfigProvider>
  );
};

export default GuideModal;
